import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { siteConfig } from '@/mocks/siteData';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('提交成功，我们会尽快与您联系！');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qtev6e3oq4jqumtqhg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('提交成功，我们会尽快与您联系！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormStatus('error');
          setFormMessage('提交失败，请稍后再试。');
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg || '提交失败，请稍后再试。');
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后再试。');
    }
  };

  const milestones = [
    { year: '2018', title: '平台成立', desc: '黄游下载正式上线，致力于为玩家提供安全可靠的成人游戏下载服务' },
    { year: '2019', title: '突破千款', desc: '游戏库突破1000款，涵盖日系、欧美、国产等多个品类' },
    { year: '2020', title: '百万用户', desc: '注册用户突破100万，成为行业领先的成人游戏下载平台' },
    { year: '2022', title: '安全升级', desc: '全面升级安全检测系统，所有资源经过多重安全扫描' },
    { year: '2024', title: '全平台覆盖', desc: '实现PC、安卓、iOS全平台支持，推出移动端专属下载通道' },
    { year: '2026', title: '行业标杆', desc: '累计服务超过860万用户，游戏下载量突破5280万次' },
  ];

  const features = [
    { title: '正版资源', desc: '严格筛选正版游戏资源，杜绝盗版和恶意软件，确保下载安全可靠', icon: 'ri-shield-check-line' },
    { title: '极速下载', desc: '多线CDN加速，全国节点覆盖，确保高速稳定的下载体验', icon: 'ri-speed-up-line' },
    { title: '多平台支持', desc: '全面支持PC、安卓等主流平台，适配不同设备的使用需求', icon: 'ri-device-line' },
    { title: '专业评测', desc: '资深玩家团队深度评测，提供客观真实的游戏推荐和攻略', icon: 'ri-star-line' },
    { title: '无广告骚扰', desc: '下载过程纯净无广告，专注提供优质的游戏下载体验', icon: 'ri-forbid-2-line' },
    { title: '客服支持', desc: '7x24小时客服在线，及时解决玩家在使用过程中遇到的问题', icon: 'ri-customer-service-2-line' },
  ];

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="min-h-screen bg-background-50 pt-16">
        {/* Page Header */}
        <section className="relative py-12 md:py-16 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20elegant%20professional%20background%2C%20modern%20corporate%20style%2C%20abstract%20geometric%20patterns%2C%20deep%20charcoal%20and%20crimson%20tones%2C%20clean%20sophisticated%20atmosphere%2C%20no%20text&width=1800&height=400&seq=about-header-bg&orientation=landscape"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/60 to-background-50"></div>
          <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '关于我们' }]} />
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-foreground-900 font-bold mt-2">
              关于我们
            </h1>
            <p className="text-sm md:text-base text-foreground-500 mt-3 max-w-2xl">
              了解{siteConfig.name}的品牌故事、发展历程与核心优势
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading text-foreground-900 font-bold mb-4">
                  品牌介绍
                </h2>
                <div className="space-y-4">
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    <strong>{siteConfig.name}</strong>成立于{siteConfig.foundingYear}年，是国内领先的正版成人游戏安全下载平台。我们致力于为广大成人游戏爱好者提供安全、便捷、高品质的游戏下载服务。
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    经过多年的深耕发展，平台已收录<strong>超过{siteConfig.stats.totalGames}款</strong>优质成人游戏，涵盖日系、欧美、国产等多个品类，覆盖RPG、SLG、视觉小说等丰富类型，满足不同玩家的多样化需求。
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    我们始终坚持<strong>正版优先、安全第一</strong>的原则，所有上架游戏资源均经过严格的安全检测和版权审核，确保玩家能够放心下载、安心畅玩。
                  </p>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    截至目前，平台已累计服务<strong>超过{siteConfig.stats.activeUsers / 10000}万注册用户</strong>，游戏下载总量突破{siteConfig.stats.totalDownloads / 10000}万次，用户满意度高达{siteConfig.stats.satisfaction}%，在业内树立了良好的口碑。
                  </p>
                </div>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20tech%20office%20workspace%20with%20gaming%20setup%2C%20warm%20ambient%20lighting%2C%20sleek%20dark%20aesthetic%2C%20professional%20creative%20environment%2C%20minimalist%20design%2C%20gaming%20monitors%20and%20equipment%2C%20clean%20composition&width=800&height=500&seq=about-brand-image&orientation=landscape"
                  alt="黄游下载办公环境"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Core Features */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 font-bold mb-8 text-center">
              核心优势
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {features.map((f, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-5 border border-background-300/30">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 text-primary-400 mb-4">
                    <i className={`${f.icon} text-xl`}></i>
                  </span>
                  <h3 className="text-base font-medium text-foreground-900 mb-2">{f.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Development Timeline */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 font-bold mb-10 text-center">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-300/40 md:-translate-x-px"></div>
              <div className="space-y-8">
                {milestones.map((m, i) => (
                  <div key={i} className={`relative flex flex-col md:flex-row gap-4 md:gap-8 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                    <div className={`flex-1 ${i % 2 === 0 ? 'md:text-right' : 'md:text-left'} pl-12 md:pl-0`}>
                      <div className={`hidden md:block ${i % 2 === 0 ? 'md:pr-8' : 'md:pl-8'}`}>
                        <span className="text-3xl font-heading font-bold text-accent-500">{m.year}</span>
                        <h3 className="text-base font-medium text-foreground-900 mt-1">{m.title}</h3>
                        <p className="text-sm text-foreground-500 mt-1 leading-relaxed">{m.desc}</p>
                      </div>
                      <div className="md:hidden">
                        <span className="text-lg font-heading font-bold text-accent-500">{m.year}</span>
                        <h3 className="text-sm font-medium text-foreground-900 mt-0.5">{m.title}</h3>
                        <p className="text-xs text-foreground-500 mt-0.5 leading-relaxed">{m.desc}</p>
                      </div>
                    </div>
                    <div className="absolute left-4 md:left-1/2 top-1 w-3 h-3 bg-accent-500 rounded-full -translate-x-1/2"></div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 text-center">
              {[
                { value: `${siteConfig.stats.totalGames}+`, label: '游戏资源' },
                { value: `${(siteConfig.stats.totalDownloads / 10000).toFixed(0)}万+`, label: '累计下载' },
                { value: `${(siteConfig.stats.activeUsers / 10000).toFixed(0)}万+`, label: '活跃用户' },
                { value: `${siteConfig.stats.satisfaction}%`, label: '用户好评率' },
              ].map((s, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-6 border border-background-300/30">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-1">{s.value}</div>
                  <div className="text-sm text-foreground-500">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-12 md:py-16 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 font-bold mb-8 text-center">
              常见问题
            </h2>
            <div className="space-y-4">
              {[
                { q: '黄游下载平台的游戏是否安全？', a: '是的，我们所有上架的游戏资源都经过严格的安全检测和版权审核，确保无毒无害、安全可靠。我们采用多重安全扫描机制，为玩家提供安心的下载环境。' },
                { q: '支持哪些平台的游戏下载？', a: '目前我们支持PC（Windows）和安卓平台的成人游戏下载。我们正在积极适配更多平台，为玩家提供更全面的服务。' },
                { q: '下载游戏需要付费吗？', a: '黄游下载平台提供免费下载服务，玩家可以免费获取正版成人游戏资源。我们致力于降低玩家的获取门槛，让更多人享受优质游戏体验。' },
                { q: '下载速度如何保证？', a: '我们部署了全国多线CDN加速节点，确保玩家无论身在何处都能获得高速稳定的下载体验。同时支持断点续传功能，大文件下载更加便捷。' },
                { q: '遇到安装问题怎么办？', a: '您可以查阅我们网站上的安装指南文章，或联系7x24小时在线客服获取帮助。我们的技术支持团队会及时为您解决问题。' },
              ].map((faq, i) => (
                <details key={i} className="group bg-background-100 rounded-lg border border-background-300/30 overflow-hidden cursor-pointer">
                  <summary className="flex items-center justify-between p-5 text-sm font-medium text-foreground-900 list-none">
                    {faq.q}
                    <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-sm text-foreground-500 leading-relaxed">
                    {faq.a}
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900 font-bold mb-2 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              如有任何问题或建议，请通过以下表单联系我们，我们会在24小时内回复
            </p>

            <form onSubmit={handleSubmit} data-readdy-form className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-300/30">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="name" className="block text-sm text-foreground-600 mb-1.5">姓名 <span className="text-primary-500">*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-300/40 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm text-foreground-600 mb-1.5">邮箱 <span className="text-primary-500">*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-300/40 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="subject" className="block text-sm text-foreground-600 mb-1.5">主题 <span className="text-primary-500">*</span></label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-300/40 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div className="mb-4">
                <label htmlFor="message" className="block text-sm text-foreground-600 mb-1.5">留言内容 <span className="text-primary-500">*</span></label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-300/40 rounded-md text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500/50 transition-colors resize-none"
                  placeholder="请输入您的留言内容（最多500字）"
                ></textarea>
              </div>

              {/* Honeypot */}
              <div className="contact-alt-field">
                <label htmlFor="phone_alt">Phone</label>
                <input
                  type="text"
                  id="phone_alt"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {formStatus !== 'idle' && (
                <div className={`mb-4 p-3 rounded-md text-sm ${
                  formStatus === 'success'
                    ? 'bg-accent-500/10 text-accent-400 border border-accent-500/20'
                    : 'bg-primary-500/10 text-primary-400 border border-primary-500/20'
                }`}>
                  {formMessage}
                </div>
              )}

              <button
                type="submit"
                className="w-full py-3 bg-primary-500 text-background-50 font-medium text-sm rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                提交留言
              </button>
            </form>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
              <div className="text-center">
                <span className="w-10 h-10 flex items-center justify-center mx-auto mb-2 text-primary-400">
                  <i className="ri-mail-line text-xl"></i>
                </span>
                <p className="text-sm text-foreground-500">{siteConfig.email}</p>
              </div>
              <div className="text-center">
                <span className="w-10 h-10 flex items-center justify-center mx-auto mb-2 text-primary-400">
                  <i className="ri-phone-line text-xl"></i>
                </span>
                <p className="text-sm text-foreground-500">{siteConfig.phone}</p>
              </div>
              <div className="text-center">
                <span className="w-10 h-10 flex items-center justify-center mx-auto mb-2 text-primary-400">
                  <i className="ri-map-pin-line text-xl"></i>
                </span>
                <p className="text-sm text-foreground-500">{siteConfig.address}</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: `关于我们 - ${siteConfig.name}`,
            description: `了解${siteConfig.name}的品牌故事、发展历程与核心优势`,
            url: `${siteConfig.url}/about`,
            mainEntity: {
              '@type': 'Organization',
              name: siteConfig.name,
              description: siteConfig.description,
              email: siteConfig.email,
              telephone: siteConfig.phone,
              address: {
                '@type': 'PostalAddress',
                streetAddress: siteConfig.address,
                addressLocality: '深圳',
                addressRegion: '广东',
                addressCountry: 'CN',
              },
              foundingDate: siteConfig.foundingYear,
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: '黄游下载平台的游戏是否安全？', acceptedAnswer: { '@type': 'Answer', text: '是的，我们所有上架的游戏资源都经过严格的安全检测和版权审核，确保无毒无害、安全可靠。我们采用多重安全扫描机制，为玩家提供安心的下载环境。' } },
              { '@type': 'Question', name: '支持哪些平台的游戏下载？', acceptedAnswer: { '@type': 'Answer', text: '目前我们支持PC（Windows）和安卓平台的成人游戏下载。我们正在积极适配更多平台，为玩家提供更全面的服务。' } },
              { '@type': 'Question', name: '下载游戏需要付费吗？', acceptedAnswer: { '@type': 'Answer', text: '黄游下载平台提供免费下载服务，玩家可以免费获取正版成人游戏资源。我们致力于降低玩家的获取门槛，让更多人享受优质游戏体验。' } },
              { '@type': 'Question', name: '下载速度如何保证？', acceptedAnswer: { '@type': 'Answer', text: '我们部署了全国多线CDN加速节点，确保玩家无论身在何处都能获得高速稳定的下载体验。同时支持断点续传功能，大文件下载更加便捷。' } },
              { '@type': 'Question', name: '遇到安装问题怎么办？', acceptedAnswer: { '@type': 'Answer', text: '您可以查阅我们网站上的安装指南文章，或联系7x24小时在线客服获取帮助。我们的技术支持团队会及时为您解决问题。' } },
            ],
          }),
        }}
      />
    </>
  );
}